/**
 * Document operations — browser-safe functions.
 *
 * For Node.js file system operations (uploadLocalFile, uploadDirectory, uploadZip),
 * import from './documents-node.js' instead.
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { createContentHash, encryptFile } from '@arbidocs/client'
import { requireData, requireOk } from '../errors.js'
import { authenticatedFetch, type AuthHeaders } from '../fetch.js'

type DocUpdateRequest = components['schemas']['DocUpdateRequest']
type DocResponse = components['schemas']['DocResponse']

// ── Pagination types ────────────────────────────────────────────────────────

/**
 * Ordering for paginated document listing.
 *
 * - `id_asc` — walk by primary key (default). Stable, matches legacy behavior.
 * - `created_desc` — newest-first. Uses a warm index; preferred for progressive
 *   loaders that want the most-recently-added docs in the first page.
 *
 * These values are derived from the backend schema docs for the `order` query
 * param on `GET /v1/document/list`. The schema types the param as `string` to
 * allow future additions without a breaking schema change, so we define the
 * narrow union here in the SDK.
 */
export type DocumentListOrder = 'id_asc' | 'created_desc'

/**
 * Response shape for paginated document listing.
 *
 * - `full` (default) — returns full `DocResponse` with decrypted `doc_metadata`
 *   and joined `doctags`. Same as the legacy single-shot endpoint.
 * - `lite` — skips the doctags JOIN and `doc_metadata` decrypt. Returned docs
 *   will have `doctags: []` and `doc_metadata: null`. Use this for listing
 *   large workspaces and fetch full per-doc data on demand via
 *   `GET /v1/document/` when a specific doc is opened.
 *
 * These values are derived from the backend schema docs for the `fields` query
 * param on `GET /v1/document/list`. The schema types the param as `string`.
 */
export type DocumentListFields = 'full' | 'lite'

/**
 * Options for paginated document listing.
 */
export interface ListPaginatedOptions {
  /**
   * Number of documents per page (applies to every page after the first).
   * @default 5000
   */
  pageSize?: number
  /**
   * Size of the first page only. When set, the initial request uses this
   * smaller limit so the caller can render something on screen before the
   * full `pageSize`-sized pages stream in. Subsequent pages fall back to
   * `pageSize`. When unset, every page uses `pageSize`.
   */
  firstPageSize?: number
  /**
   * Sort order for pagination.
   * @default 'id_asc'
   */
  order?: DocumentListOrder
  /**
   * Response shape. Use `'lite'` to skip doctags and doc_metadata decrypt.
   * When `fields: 'lite'`, returned docs have `doctags: []` and `doc_metadata: null`.
   * Fetch full per-doc data on demand when a specific doc is opened.
   * @default 'full'
   */
  fields?: DocumentListFields
  /**
   * AbortSignal to cancel iteration mid-stream.
   */
  signal?: AbortSignal
  /**
   * Number of pages kept in flight concurrently. A higher value hides more
   * backend + network latency between pages but increases peak backend load
   * and memory for not-yet-consumed pages. Clamped to `[1, MAX_PAGES]`.
   * @default 1
   */
  lookahead?: number
}

/**
 * Options for `listAll` — collects all pages into a single array.
 */
export interface ListAllOptions extends Omit<ListPaginatedOptions, 'signal'> {
  signal?: AbortSignal
}

/** Hard cap on pages to guard against runaway loops on misbehaving backends. */
export const MAX_PAGES = 400 // 5000 * 400 = 2M docs

// ── Shared constants ────────────────────────────────────────────────────────

/** File extensions supported for document upload. */
export const SUPPORTED_EXTENSIONS = new Set([
  '.pdf',
  '.txt',
  '.md',
  '.html',
  '.htm',
  '.doc',
  '.docx',
  '.rtf',
  '.ppt',
  '.pptx',
  '.xls',
  '.xlsx',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.tiff',
  '.tif',
  '.webp',
  // Video (store only, no parsing)
  '.mp4',
  '.mpeg',
  '.mpg',
  '.webm',
  '.mov',
  '.avi',
  '.mkv',
  // Audio (store only, no parsing)
  '.mp3',
  '.wav',
  '.m4a',
  '.aac',
  '.ogg',
  '.flac',
  // Email formats (parsed for content)
  '.eml',
  '.msg',
  '.mbox',
  '.pst',
  '.ost',
])

// ── Shared utilities ────────────────────────────────────────────────────────

/**
 * Normalize a folder path for storage. Preserves Unicode characters, spaces,
 * and other filesystem-valid characters faithfully for e-discovery.
 *
 * Only strips control characters and trims leading/trailing whitespace.
 * Normalizes path separators (backslash → forward slash) and collapses
 * consecutive slashes.
 */
export function sanitizeFolderPath(folderPath: string): string {
  return (
    folderPath
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1f\x7f]/g, '') // strip control characters
      .replace(/\\/g, '/') // normalize backslash to forward slash
      .replace(/\/{2,}/g, '/') // collapse consecutive slashes
      .replace(/^\/|\/$/g, '') // strip leading/trailing slashes
      .trim()
  )
}

// ── Types ───────────────────────────────────────────────────────────────────

export interface UploadOptions {
  folder?: string
  configExtId?: string
  /** Work-product type for the uploaded doc — ``source`` (default),
   *  ``skill``, ``memory``, ``artifact``. Skills must be uploaded with
   *  ``wp_type='skill'`` AND in a ``skills/<slug>`` folder so the
   *  backend's ``SkillManager`` recognises them. */
  wpType?: 'source' | 'skill' | 'memory' | 'artifact' | 'webpage'
}

export interface SkippedFile {
  file_name: string
  reason: string
}

export interface UploadResult {
  doc_ext_ids?: string[]
  batch_id?: string | null
  skipped?: SkippedFile[]
}

export interface UploadBatchResult {
  doc_ext_ids: string[]
  skipped: SkippedFile[]
  folders: Map<string, { fileCount: number; doc_ext_ids: string[]; skipped: SkippedFile[] }>
}

// ── API operations (browser-safe) ───────────────────────────────────────────

export async function listDocuments(arbi: ArbiClient) {
  return requireData(await arbi.fetch.GET('/v1/document/list'), 'Failed to fetch documents')
}

/**
 * Async iterator that yields pages of documents with a configurable lookahead.
 *
 * Uses `limit`/`offset` pagination. A FIFO queue of up to `lookahead` requests
 * is kept in flight: as soon as a page is awaited off the queue the next
 * request is enqueued, so the backend is continuously working on the next
 * page while the consumer processes the current one. The default of 1 keeps
 * one page in flight at a time (classic sequential pagination). Raise it when
 * you want to pipeline — each extra slot adds one more concurrent backend
 * scan and one more not-yet-consumed page held in memory.
 *
 * Pair `firstPageSize` with a larger `pageSize` when you need the initial
 * page on screen fast: e.g. `firstPageSize: 500, pageSize: 2000` renders the
 * first 500 rows in a fifth of the time of a single 2500-row request, then
 * streams in 2000-row pages after.
 *
 * Iteration stops at the first short page (length < `pageSize`) — in-flight
 * requests past that point are discarded. `MAX_PAGES` is a hard cap on the
 * number of requests issued.
 *
 * @example
 * ```ts
 * for await (const page of listPaginated(arbi, { pageSize: 5000, order: 'created_desc', fields: 'lite' })) {
 *   // page: DocResponse[] — render incrementally as pages arrive
 * }
 * ```
 *
 * @param arbi - Authenticated ArbiClient
 * @param options - Pagination options (pageSize, order, fields, signal, lookahead)
 * @yields Pages of documents until the backend returns a short page or signal is aborted
 */
export async function* listPaginated(
  arbi: ArbiClient,
  options: ListPaginatedOptions = {}
): AsyncGenerator<DocResponse[]> {
  const {
    pageSize = 5000,
    firstPageSize,
    order = 'id_asc',
    fields,
    signal,
    lookahead = 1,
  } = options

  const fetchPage = async (pageOffset: number, limit: number): Promise<DocResponse[]> => {
    const query: { limit: number; offset: number; order: string; fields?: string } = {
      limit,
      offset: pageOffset,
      order,
    }
    if (fields) query.fields = fields

    const { data, error } = await arbi.fetch.GET('/v1/document/list', {
      params: { query },
      signal,
    })

    if (error) {
      throw new Error(typeof error === 'string' ? error : JSON.stringify(error))
    }

    return (data as DocResponse[] | undefined) ?? []
  }

  if (signal?.aborted) return

  // Clamp to [1, MAX_PAGES] — below 1 makes no forward progress, above
  // MAX_PAGES would just get capped by the issuance limit anyway.
  const depth = Math.max(1, Math.min(lookahead, MAX_PAGES))

  let issued = 0
  let pagesYielded = 0
  let nextOffsetToIssue = 0
  let done = false
  // Track the limit we asked for alongside the promise so the short-page
  // check at the consumer compares against the right number. Mixing a
  // small first page with larger subsequent pages means a full first
  // page (length === firstPageSize) would otherwise look short.
  const queue: Array<{ limit: number; promise: Promise<DocResponse[]> }> = []

  const tryEnqueue = () => {
    while (!done && queue.length < depth && issued < MAX_PAGES) {
      const limit = issued === 0 && firstPageSize !== undefined ? firstPageSize : pageSize
      const promise = fetchPage(nextOffsetToIssue, limit)
      // Attach a no-op rejection handler so promises we end up discarding
      // (abort mid-iteration, or the iteration stops on a short page with
      // pages still in flight) don't surface as unhandled rejections. The
      // original promise is still rejected when awaited from the queue —
      // `.catch()` attaches a second handler, it does not replace the promise.
      promise.catch(() => {})
      queue.push({ limit, promise })
      nextOffsetToIssue += limit
      issued++
    }
  }

  tryEnqueue()

  while (queue.length > 0 && !signal?.aborted) {
    const { limit, promise } = queue.shift()!
    const page = await promise

    if (signal?.aborted) return

    const isShort = page.length < limit
    if (isShort) done = true

    // Refill the queue BEFORE yielding so the next prefetch hits the network
    // while the consumer is still processing this page.
    tryEnqueue()

    if (page.length > 0) {
      yield page
      pagesYielded++
    }

    if (isShort) return
    if (pagesYielded >= MAX_PAGES) break
  }

  if (!signal?.aborted && !done && pagesYielded >= MAX_PAGES) {
    // Use console.warn since SDK has no built-in logger and this is a safety guard
    console.warn(
      `[arbi-sdk] listPaginated hit MAX_PAGES (${MAX_PAGES}) — stopped. ` +
        `Workspace may have more than ${MAX_PAGES * pageSize} documents.`
    )
  }
}

/**
 * Collect all workspace documents into a single array using sequential pagination.
 *
 * Uses `listPaginated` internally. Warns if `MAX_PAGES` is hit.
 *
 * **Note:** When `fields: 'lite'` is passed, returned docs have `doctags: []`
 * and `doc_metadata: null`. Fetch full per-doc data on demand when a specific
 * doc is opened.
 *
 * @example
 * ```ts
 * const docs = await listAll(arbi, { fields: 'lite', order: 'created_desc' })
 * ```
 */
export async function listAll(
  arbi: ArbiClient,
  options: ListAllOptions = {}
): Promise<DocResponse[]> {
  const all: DocResponse[] = []
  const seen = new Set<string>()

  for await (const page of listPaginated(arbi, options)) {
    for (const doc of page) {
      const id = doc.external_id
      if (id && !seen.has(id)) {
        seen.add(id)
        all.push(doc)
      }
    }
  }

  return all
}

export async function getDocuments(arbi: ArbiClient, externalIds: string[]) {
  return requireData(
    await arbi.fetch.GET('/v1/document/', { params: { query: { external_ids: externalIds } } }),
    'Failed to fetch documents'
  )
}

export async function deleteDocuments(arbi: ArbiClient, externalIds: string[]) {
  requireOk(
    await arbi.fetch.DELETE('/v1/document/', { body: { external_ids: externalIds } }),
    'Failed to delete documents'
  )
}

export async function updateDocuments(arbi: ArbiClient, documents: DocUpdateRequest[]) {
  return requireData(
    await arbi.fetch.PATCH('/v1/document/', { body: { documents } }),
    'Failed to update documents'
  )
}

/**
 * Kick off a URL-based document ingestion. The active workspace is resolved
 * server-side from the open session — there is no ``workspace_ext_id`` query
 * param on this endpoint.
 */
export async function uploadUrl(arbi: ArbiClient, urls: string[], shared = false) {
  return requireData(
    await arbi.fetch.POST('/v1/document/upload-url', {
      params: {
        query: { urls, shared },
      },
    }),
    'Failed to upload from URLs'
  )
}

/**
 * Get parsed document content.
 */
export async function getParsedContent(
  auth: AuthHeaders,
  docId: string,
  stage: string
): Promise<Record<string, unknown>> {
  const res = await authenticatedFetch({
    ...auth,
    path: `/v1/document/${docId}/parsed-${stage}`,
  })
  return res.json() as Promise<Record<string, unknown>>
}

/**
 * Batch-fetch first-page thumbnails for many documents in one request.
 *
 * Returns a map of document external id → `data:image/webp;base64` URI, or
 * `null` when a document has no thumbnail (media, not yet generated, or not
 * accessible to the caller). Purpose-built for a gallery grid that loads a whole
 * window of tiles in a single call rather than one request per document.
 */
export async function getThumbnails(
  auth: AuthHeaders,
  docIds: string[]
): Promise<Record<string, string | null>> {
  if (docIds.length === 0) return {}
  const res = await authenticatedFetch({
    ...auth,
    path: `/v1/document/thumbnails`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ document_ext_ids: docIds }),
  })
  const json = (await res.json()) as { thumbnails?: Record<string, string | null> }
  return json.thumbnails ?? {}
}

/**
 * Upload a file. Uses raw fetch for multipart upload. The active workspace is
 * resolved server-side from the open session.
 */
export async function uploadFile(
  auth: AuthHeaders,
  fileData: Blob,
  fileName: string,
  options?: UploadOptions
): Promise<UploadResult> {
  const formData = new FormData()
  formData.append('files', fileData, fileName)

  const params = new URLSearchParams()
  if (options?.folder) params.set('folder', sanitizeFolderPath(options.folder))
  if (options?.configExtId) params.set('config_ext_id', options.configExtId)
  if (options?.wpType) params.set('wp_type', options.wpType)

  const qs = params.toString()
  const res = await authenticatedFetch({
    ...auth,
    path: qs ? `/v1/document/upload?${qs}` : `/v1/document/upload`,
    method: 'POST',
    body: formData,
  })

  return res.json() as Promise<UploadResult>
}

/**
 * Upload multiple files in a single FormData request with an optional folder
 * parameter. Workspace is resolved from the session, same as ``uploadFile``.
 */
export async function uploadFiles(
  auth: AuthHeaders,
  files: Array<{ data: Blob; name: string }>,
  options?: UploadOptions
): Promise<UploadResult> {
  const formData = new FormData()
  for (const f of files) formData.append('files', f.data, f.name)

  const params = new URLSearchParams()
  if (options?.folder) params.set('folder', sanitizeFolderPath(options.folder))
  if (options?.configExtId) params.set('config_ext_id', options.configExtId)
  if (options?.wpType) params.set('wp_type', options.wpType)

  const qs = params.toString()
  const res = await authenticatedFetch({
    ...auth,
    path: qs ? `/v1/document/upload?${qs}` : `/v1/document/upload`,
    method: 'POST',
    body: formData,
  })

  return res.json() as Promise<UploadResult>
}

/**
 * Download a document. Returns the response for the caller to handle.
 */
export async function downloadDocument(auth: AuthHeaders, docId: string): Promise<Response> {
  return authenticatedFetch({
    ...auth,
    path: `/v1/document/${docId}/download`,
  })
}

// ── Direct-to-MinIO upload (browser-safe) ──────────────────────────────────

/**
 * One file prepared for the direct upload flow. The caller supplies raw,
 * pre-encryption bytes and a basename; the helper handles hashing + SecretBox
 * encryption + transport.
 */
export interface DirectUploadFile {
  name: string
  /** Raw file bytes (pre-encryption). */
  data: Uint8Array
  /** MIME type (e.g. ``application/pdf``). Optional — server will guess from name if omitted. */
  contentType?: string
}

export interface UploadDirectOptions {
  shared?: boolean
  folder?: string
  configExtId?: string
  parentExtId?: string
  wpType?: string
  tagExtId?: string
  /** Expiry for presigned PUT URLs, in seconds. Defaults to server default (1h). */
  presignExpiresIn?: number
  /**
   * Fired as each file's ciphertext is uploaded. In the browser this yields
   * byte-level progress (XMLHttpRequest upload.progress); in Node this fires
   * once per file with `loaded === total` after each PUT completes.
   *
   * - `fileIndex` indexes into the `files` array passed to the helper.
   * - `fileName` is the corresponding `DirectUploadFile.name`.
   * - `loaded` / `total` are bytes of the *ciphertext* (= raw + 40).
   */
  onBytesProgress?: (progress: DirectUploadProgress) => void
}

export interface DirectUploadProgress {
  fileIndex: number
  fileName: string
  loaded: number
  total: number
}

export interface UploadDirectResult {
  /** Doc ext IDs that were committed and will process through the parse pipeline. */
  doc_ext_ids: string[]
  /**
   * Files that were not uploaded — either rejected at init (unsupported,
   * empty, duplicate, quota_exceeded) or reported back by the commit step
   * (no PUT landed, unknown ext id). Same shape as the legacy multipart
   * ``skipped`` list so callers can treat them uniformly.
   */
  skipped: SkippedFile[]
}

type UploadInitResponse = components['schemas']['UploadInitResponse']

/**
 * Structural subset of ``ArbiClient`` that ``uploadDocumentsDirect`` actually
 * uses. The helper only needs the openapi-fetch client, so consumers that don't
 * have a full ``ArbiClient`` (e.g. the React frontend, which wires openapi-fetch
 * through its own middleware) can pass ``{ fetch }`` directly without a cast.
 */
export type DirectUploadClient = Pick<ArbiClient, 'fetch'>

/**
 * Minimal extension → MIME mapping to avoid a `mime-types` dep.
 *
 * Browser callers don't usually need this — a File's `.type` is already
 * populated by the browser — but CLI/Node callers construct Blobs from raw
 * bytes and must set the MIME explicitly, otherwise multipart parts land as
 * `application/octet-stream`. Exported for both consumers so the mapping
 * lives in one place.
 */
export function mimeFromName(name: string): string | undefined {
  const dot = name.lastIndexOf('.')
  const ext = dot === -1 ? '' : name.slice(dot).toLowerCase()
  switch (ext) {
    case '.pdf':
      return 'application/pdf'
    case '.txt':
      return 'text/plain'
    case '.md':
      return 'text/markdown'
    case '.html':
    case '.htm':
      return 'text/html'
    case '.doc':
      return 'application/msword'
    case '.docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case '.xls':
      return 'application/vnd.ms-excel'
    case '.xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case '.ppt':
      return 'application/vnd.ms-powerpoint'
    case '.pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    case '.eml':
      return 'message/rfc822'
    case '.pst':
    case '.ost':
      return 'application/vnd.ms-outlook'
    default:
      return undefined
  }
}

/**
 * Minimal XHR shape we rely on. Declared locally so the SDK tsconfig can stay
 * lib-agnostic (no "dom" dependency) — this keeps the package buildable for
 * Node consumers while still compiling against the browser runtime.
 */
interface XhrUploadEvent {
  lengthComputable: boolean
  loaded: number
  total: number
}
interface XhrLike {
  upload: { addEventListener(event: 'progress', cb: (e: XhrUploadEvent) => void): void }
  status: number
  responseText: string
  open(method: string, url: string): void
  send(body: Uint8Array): void
  addEventListener(event: 'load' | 'error' | 'abort', cb: () => void): void
}
type XhrCtor = new () => XhrLike

/**
 * PUT a body to a presigned URL. Uses XMLHttpRequest in the browser so that
 * `upload.progress` events are available; falls back to fetch elsewhere.
 * `onProgress` is only guaranteed to fire in the XHR path — Node callers get
 * a single synthetic 100 % tick from the direct-upload driver instead.
 */
async function presignedPut(
  url: string,
  body: Uint8Array,
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  const XhrGlobal = (globalThis as { XMLHttpRequest?: XhrCtor }).XMLHttpRequest
  if (XhrGlobal) {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XhrGlobal()
      xhr.open('PUT', url)
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) onProgress(e.loaded, e.total)
        })
      }
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          const snippet = (xhr.responseText || '').slice(0, 200)
          reject(new Error(`Presigned PUT failed: ${xhr.status} ${snippet}`))
        }
      })
      xhr.addEventListener('error', () => reject(new Error('Presigned PUT network error')))
      xhr.addEventListener('abort', () => reject(new Error('Presigned PUT aborted')))
      xhr.send(body)
    })
    return
  }

  const putRes = await fetch(url, { method: 'PUT', body })
  if (!putRes.ok) {
    const snippet = await putRes.text().catch(() => '')
    throw new Error(`Presigned PUT failed: ${putRes.status} ${snippet.slice(0, 200)}`)
  }
}

/**
 * Upload files via the direct-to-MinIO flow (SecretBox on the client,
 * presigned PUT to nginx→MinIO, then commit).
 *
 * Three phases:
 *   1. ``POST /v1/document/upload-init`` — declare files, receive presigned PUT
 *      URLs for every ``uploading`` slot. Duplicate / unsupported / empty /
 *      quota slots are surfaced as ``skipped`` in the result.
 *   2. ``PUT {upload_url}`` — encrypt each file with ``workspaceKey`` and PUT
 *      the ciphertext directly to MinIO via the public ``/arbi-files/`` route.
 *      arbi-app is not in the byte path.
 *   3. ``POST /v1/document/upload-commit`` — HEAD-verify objects landed and
 *      flip rows to ``queued``. Failures here are also reported as skipped
 *      (not thrown) so a partial-upload error for one file doesn't lose the
 *      doc ids for the rest.
 *
 * The helper is browser-safe — it only touches ``Uint8Array`` / ``fetch``. Node
 * callers that want to upload files from disk should use the wrapper in
 * ``documents-node`` which reads ``fs`` into the ``data`` field.
 */
export async function uploadDocumentsDirect(
  arbi: DirectUploadClient,
  workspaceKey: Uint8Array,
  files: DirectUploadFile[],
  options?: UploadDirectOptions
): Promise<UploadDirectResult> {
  if (files.length === 0) return { doc_ext_ids: [], skipped: [] }

  // Phase 1: upload-init.
  //
  // SecretBox envelope is exactly 40 bytes — 24-byte nonce prefix + 16-byte
  // Poly1305 tag — so the ciphertext we will PUT is `raw.length + 40`. The
  // server validates this exact relationship at init and HEAD-checks the
  // actual MinIO Content-Length against this declaration at commit, so any
  // mid-PUT truncation, quota-bypass attempt, or wrong-content substitution
  // gets rejected before the doc is queued.
  const SECRETBOX_ENVELOPE_OVERHEAD = 40
  const declared = files.map((f) => ({
    file_name: f.name,
    file_size: f.data.length,
    encrypted_file_size: f.data.length + SECRETBOX_ENVELOPE_OVERHEAD,
    content_hash: createContentHash(f.data, workspaceKey),
    content_type: f.contentType ?? null,
  }))

  const initBody = {
    files: declared,
    shared: options?.shared !== false,
    config_ext_id: options?.configExtId ?? null,
    parent_ext_id: options?.parentExtId ?? null,
    wp_type: options?.wpType ?? null,
    tag_ext_id: options?.tagExtId ?? null,
    folder: options?.folder ? sanitizeFolderPath(options.folder) : null,
    presign_expires_in: options?.presignExpiresIn ?? 3600,
  }

  const initResult = requireData(
    await arbi.fetch.POST('/v1/document/upload-init', { body: initBody }),
    'Failed to initialize direct upload'
  ) as UploadInitResponse

  const items = initResult.items ?? []

  // Match init items back to their raw bytes by name. Duplicate basenames in
  // one batch are allowed by the server but ambiguous here — resolve in
  // declaration order by draining a per-name queue.
  const bytesByName = new Map<string, Uint8Array[]>()
  for (const f of files) {
    if (!bytesByName.has(f.name)) bytesByName.set(f.name, [])
    bytesByName.get(f.name)!.push(f.data)
  }

  const skipped: SkippedFile[] = []
  const toCommit: string[] = []

  // Map each item back to its caller-side index so progress callbacks can
  // identify files by the same index the caller declared them under.
  const indexByName = new Map<string, number[]>()
  files.forEach((f, i) => {
    if (!indexByName.has(f.name)) indexByName.set(f.name, [])
    indexByName.get(f.name)!.push(i)
  })

  // Phase 2: PUT encrypted bytes for every accepted slot.
  for (const item of items) {
    if (item.status !== 'uploading' || !item.upload_url || !item.doc_ext_id) {
      skipped.push({
        file_name: item.file_name,
        reason: item.reason || item.status,
      })
      continue
    }

    const queue = bytesByName.get(item.file_name)
    const idxQueue = indexByName.get(item.file_name)
    if (!queue || queue.length === 0 || !idxQueue || idxQueue.length === 0) {
      // Server handed us a slot for a file we didn't declare — shouldn't
      // happen, but fail loudly rather than silently drop.
      throw new Error(`upload-init returned slot for unknown file: ${item.file_name}`)
    }
    const raw = queue.shift()!
    const fileIndex = idxQueue.shift()!
    const ciphertext = encryptFile(raw, workspaceKey)
    const total = ciphertext.length

    try {
      await presignedPut(item.upload_url, ciphertext, (loaded, putTotal) => {
        options?.onBytesProgress?.({
          fileIndex,
          fileName: item.file_name,
          loaded,
          total: putTotal,
        })
      })
    } catch (err) {
      throw new Error(
        `Presigned PUT for ${item.doc_ext_id} failed: ${err instanceof Error ? err.message : String(err)}`
      )
    }
    // Emit a final 100 % tick so Node callers (no XHR progress) still see
    // per-file completion, and browser callers get a guaranteed final value.
    options?.onBytesProgress?.({
      fileIndex,
      fileName: item.file_name,
      loaded: total,
      total,
    })
    toCommit.push(item.doc_ext_id)
  }

  if (toCommit.length === 0) {
    return { doc_ext_ids: [], skipped }
  }

  // Phase 3: upload-commit.
  const commitResult = requireData(
    await arbi.fetch.POST('/v1/document/upload-commit', {
      body: {
        doc_ext_ids: toCommit,
        tag_ext_id: options?.tagExtId ?? null,
      },
    }),
    'Failed to commit direct upload'
  )

  return {
    doc_ext_ids: commitResult.doc_ext_ids ?? [],
    skipped: [...skipped, ...(commitResult.skipped ?? [])],
  }
}
